import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchProductDetail, lookupPricing } from "@/lib/api";
import { Loader2, AlertCircle, Star, ShoppingCart, Package, Beaker, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";
import ProductReviews from "@/components/ProductReviews";
import ProductProtocolsSection from "@/components/ProductProtocolsSection";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Product detail rendered INSIDE the dealer-page template's left column
 * (so the right sidebar stays intact). Self-contained: fetches product +
 * role pricing, manages qty, writes to the per-dealer cart, then calls
 * `onAdded()` so the parent can open the cart sheet.
 */
export default function ProductDetailContent({ dealerSlug, productSlug, user, onAdded, onBack }) {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeMedia, setActiveMedia] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const pres = await fetchProductDetail(productSlug);
        if (!active) return;
        setProduct(pres?.product || null);
        setActiveMedia(null);
        setError(null);
      } catch (e) {
        if (!active) return;
        setError(e?.response?.data?.detail || e.message || "Could not load product");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [productSlug]);

  useEffect(() => {
    const tier = (user?.role_name || user?.role || "").toLowerCase();
    if (!tier || !product?.id) return;
    let active = true;
    (async () => {
      try {
        const res = await lookupPricing({ productRecordIds: [product.id], tier });
        if (!active) return;
        setPricing((res?.items || [])[0] || null);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.role_name, user?.role, product?.id]);

  function handleAddToCart() {
    if (!product) return;
    try {
      const key = `postcue.cart.${dealerSlug}`;
      const raw = localStorage.getItem(key);
      const cart = raw ? JSON.parse(raw) : {};
      cart[product.id] = (cart[product.id] || 0) + (Number(qty) || 1);
      localStorage.setItem(key, JSON.stringify(cart));
    } catch {
      /* ignore */
    }
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/d/${dealerSlug}`)}`);
      return;
    }
    onAdded?.();
  }

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="py-20 flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="text-zinc-400 mb-3" size={32} />
        <div className="font-semibold text-zinc-900">Product not found</div>
        <button onClick={onBack} className="text-sm text-violet-700 mt-4 hover:underline">
          ← Back to shop
        </button>
      </div>
    );
  }

  const msrp = Number(product.price || 0);
  const rolePrice = pricing?.role_price ? Number(pricing.role_price) : null;
  const showRolePrice =
    rolePrice !== null && rolePrice > 0 && Math.abs(rolePrice - msrp) > 0.005;
  const tier = (user?.role_name || user?.role || "").toLowerCase();
  const inlineSale = resolveSale(product.sale || {}, tier);
  // Pricing-table sale (from /postcue/pricing) takes precedence when active.
  const tableSale =
    pricing?.on_sale && Number(pricing?.sale_price || 0) > 0
      ? Number(pricing.sale_price)
      : null;
  const saleActive = tableSale != null || inlineSale.active;
  const salePrice = tableSale != null ? tableSale : inlineSale.salePrice;
  const saleEnds = inlineSale.endsDisplay || "";
  const finalPrice = saleActive ? salePrice : showRolePrice ? rolePrice : msrp;

  // Media gallery: prefer the typed `media` list from the API (each item is
  // {type: 'image'|'video'|'embed', url}). Falls back to the main image.
  const media =
    product.media && product.media.length
      ? product.media
      : product.image_url
        ? [{ type: "image", url: product.image_url }]
        : [];
  const current = activeMedia || media[0] || null;
  const hasGallery = media.length > 1;

  return (
    <div className="space-y-5" data-testid="product-detail-content">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline"
        data-testid="product-back-to-shop"
      >
        <ChevronLeft size={15} /> Back to shop
      </button>

      {/* Hero block */}
      <section className="grid md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-3">
          <div
            className="aspect-square rounded-2xl bg-white border border-zinc-200 flex items-center justify-center overflow-hidden"
            data-testid="product-main-media"
          >
            {current?.type === "video" ? (
              <video
                key={current.url}
                src={current.url}
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
                data-testid="product-video-player"
              />
            ) : current?.type === "embed" ? (
              <iframe
                key={current.url}
                src={toEmbedUrl(current.url)}
                title={product.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="product-video-embed"
              />
            ) : current?.url ? (
              <img
                src={current.url}
                alt={product.name}
                className="w-full h-full object-contain"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <Package size={64} className="text-zinc-300" />
            )}
          </div>

          {hasGallery && (
            <div
              className="flex items-center gap-2.5 overflow-x-auto pb-1"
              data-testid="product-thumbnail-strip"
            >
              {media.map((m, i) => {
                const isActive = current?.url === m.url;
                return (
                  <button
                    key={`${m.type}-${i}`}
                    type="button"
                    onClick={() => setActiveMedia(m)}
                    className={`relative shrink-0 h-16 w-16 rounded-xl overflow-hidden bg-white border-2 transition-colors ${
                      isActive
                        ? "border-violet-600 ring-1 ring-violet-200"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`product-thumb-${i}`}
                    aria-label={
                      m.type === "video" ? "Play product video" : `View image ${i + 1}`
                    }
                  >
                    {m.type === "video" || m.type === "embed" ? (
                      <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                        <Play size={20} className="text-white fill-white" />
                      </div>
                    ) : (
                      <img
                        src={m.url}
                        alt={`${product.name} ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <h1
            className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight tracking-tight"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            data-testid="product-name"
          >
            {product.name}
          </h1>
          {product.excerpt && (
            <div className="text-sm text-zinc-700 mt-2 font-medium leading-relaxed space-y-1" data-testid="product-excerpt">
              {(product.excerpt_raw || product.excerpt).split(/\r?\n/).filter((l) => l.trim()).map((line, i) => (
                <p key={i}>
                  {line.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
                    seg.startsWith("**") ? <strong key={j} className="text-zinc-900 font-bold">{seg.slice(2, -2)}</strong> : seg
                  )}
                </p>
              ))}
            </div>
          )}
          {product.blurb && (
            <p className="text-sm text-zinc-600 mt-2 border-l-2 border-violet-300 pl-3 italic" data-testid="product-blurb">
              {product.blurb}
            </p>
          )}

          {product.promo && product.promo.active && (
            <span
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-rose-100 text-rose-700 self-start"
              data-testid="product-promo-badge"
            >
              🎁 {product.promo.label}
            </span>
          )}

          <div className="flex items-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={14}
                className={
                  i <= Math.round(product.rating_avg || 5)
                    ? "fill-amber-400 text-amber-400"
                    : "text-zinc-300"
                }
              />
            ))}
            <span className="text-xs text-zinc-500 ml-1">
              {product.rating_count > 0
                ? `${Number(product.rating_avg || 5).toFixed(1)} (${product.rating_count})`
                : "Be the first to review"}
            </span>
          </div>

          <div className="mt-5 rounded-2xl bg-white border border-zinc-200 p-4 space-y-1.5">
            {saleActive && saleEnds && (
              <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600">
                Sale ends {saleEnds}
              </div>
            )}
            {saleActive && !saleEnds && (
              <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600">
                On sale
              </div>
            )}
            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                className="text-3xl font-extrabold text-violet-700"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                data-testid="product-final-price"
              >
                {fmtUSD(finalPrice)}
              </span>
              {(saleActive || showRolePrice) && (
                <span className="text-sm text-zinc-400 line-through">{fmtUSD(msrp)}</span>
              )}
            </div>
            {showRolePrice && !saleActive && (
              <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                Your {tier} price
              </div>
            )}
            {saleActive && showRolePrice && (
              <div className="text-[11px] text-zinc-500">
                Reg. {tier} price {fmtUSD(rolePrice)}
              </div>
            )}
            <div className="text-[11px] text-zinc-500 pt-1">
              {Number(product.inventory || 0) > 0 ? (
                <span className="text-emerald-600 font-semibold">In stock</span>
              ) : (
                <span className="text-rose-600 font-semibold">Out of stock</span>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <button
                onClick={() => setQty((q) => Math.max(1, (Number(q) || 1) - 1))}
                className="h-11 w-11 text-zinc-600 hover:bg-zinc-50 text-lg"
                aria-label="Decrease quantity"
              >
                −
              </button>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                className="w-12 h-11 text-center text-sm font-semibold bg-white outline-none"
                data-testid="product-qty"
              />
              <button
                onClick={() => setQty((q) => (Number(q) || 1) + 1)}
                className="h-11 w-11 text-zinc-600 hover:bg-zinc-50 text-lg"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={Number(product.inventory || 0) <= 0}
              className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
              data-testid="product-add-cart"
            >
              <ShoppingCart size={15} />
              Add to cart · {fmtUSD(finalPrice * (Number(qty) || 1))}
            </button>
          </div>
        </div>
      </section>

      {product.bundle_items?.length > 0 && (
        <section className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5" data-testid="bundle-items">
          <h2 className="text-base md:text-lg font-bold text-zinc-900" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
            What's in this bundle
            <span className="ml-2 text-xs font-medium text-zinc-500">{product.bundle_items.length} products</span>
          </h2>
          <ul className="mt-3 divide-y divide-zinc-100">
            {product.bundle_items.map((it) => (
              <li key={it.id}>
                <Link
                  to={`/d/${dealerSlug}?view=product&p=${encodeURIComponent(it.slug)}`}
                  className="flex items-center gap-3 py-2.5 group"
                  data-testid={`bundle-item-${it.id}`}
                >
                  <div className="h-14 w-14 rounded-xl bg-zinc-50 border border-zinc-200 overflow-hidden shrink-0">
                    {it.image_url && <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" loading="lazy" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-900 group-hover:text-violet-700 truncate">{it.name}</div>
                    {it.excerpt && <div className="text-xs text-zinc-500 line-clamp-2">{it.excerpt}</div>}
                  </div>
                  <ChevronRight size={16} className="text-zinc-300 group-hover:text-violet-500 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ProductProtocolsSection productRecordId={product.id} dealerSlug={dealerSlug} />

      <ProductReviews productRecordId={product.id} />

      {product.post_content_html && (() => {
        const { summary, hasMore } = splitFirstBlock(product.post_content_html);
        const showFull = descExpanded || !hasMore;
        return (
          <section
            className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5"
            data-testid="product-description"
          >
            <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3">
              Description
            </h2>
            <div
              className="prose prose-sm max-w-none text-zinc-800"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(showFull ? product.post_content_html : summary),
              }}
            />
            {hasMore && (
              <button
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-3 text-sm font-semibold text-violet-700 hover:underline"
                data-testid="product-desc-toggle"
              >
                {descExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </section>
        );
      })()}

      {product.ingredients?.length > 0 && (
        <section
          className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5"
          data-testid="product-ingredients"
        >
          <div className="flex items-center gap-2 mb-3">
            <Beaker size={16} className="text-violet-700" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-violet-700">
              Key ingredients
            </h2>
            <span className="text-[11px] text-zinc-500">{product.ingredients.length} active</span>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2.5">
            {product.ingredients.map((ing) => (
              <li
                key={ing.id || ing.name}
                className="rounded-xl bg-zinc-50 border border-zinc-100 p-3 flex items-start gap-3"
              >
                {ing.image_url ? (
                  <img
                    src={ing.image_url}
                    alt={ing.name}
                    className="w-12 h-12 rounded-lg object-cover bg-white shrink-0 ring-1 ring-zinc-100"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900">{ing.name}</div>
                  {ing.benefit && (
                    <div className="text-xs text-zinc-600 mt-1 leading-snug">{ing.benefit}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function resolveSale(saleMap, tier) {
  if (!saleMap || !tier) return { active: false };
  const variants = [tier, tier.toLowerCase(), capitalize(tier)];
  for (const v of variants) {
    const sale = saleMap[`salePrice${v}`];
    if (sale === undefined || sale === "" || Number(sale) <= 0) continue;
    const from = saleMap[`_sale_price_dates_from_${v}`];
    const to = saleMap[`_sale_price_dates_to_${v}`];
    if (isWithinSale(from, to)) {
      return { active: true, salePrice: Number(sale), endsDisplay: to ? formatDate(to) : "" };
    }
  }
  return { active: false };
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Convert a YouTube/Vimeo watch URL into an embeddable iframe URL.
function toEmbedUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith("/embed/")) return url;
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

function isWithinSale(from, to) {
  if (!from && !to) return false;
  const now = new Date();
  if (from) {
    const f = new Date(from);
    if (!isNaN(f) && now < f) return false;
  }
  if (to) {
    const t = new Date(to);
    if (!isNaN(t)) {
      const endOfDay = new Date(t);
      endOfDay.setHours(23, 59, 59, 999);
      if (now > endOfDay) return false;
    }
  }
  return true;
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return String(d);
  }
}

// Returns the first meaningful block (paragraph) of an HTML string as the
// collapsed "summary", plus whether there is more content to reveal.
// Airtable authors content wrapped in a single outer <section>/<div>, so we
// descend into single-child wrappers until we reach the real block list.
function splitFirstBlock(html) {
  if (!html) return { summary: "", hasMore: false };
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let container = doc.body;
    for (let i = 0; i < 5; i++) {
      const kids = Array.from(container.children).filter(
        (b) => (b.textContent || "").trim().length > 0
      );
      if (kids.length === 1 && kids[0].children.length > 0) {
        container = kids[0];
        continue;
      }
      break;
    }
    const blocks = Array.from(container.children);
    const idx = blocks.findIndex((b) => (b.textContent || "").trim().length > 0);
    if (idx === -1) return { summary: html, hasMore: false };
    const summary = blocks[idx].outerHTML;
    const hasMore = blocks
      .slice(idx + 1)
      .some((b) => (b.textContent || "").trim().length > 0);
    return { summary, hasMore };
  } catch {
    return { summary: html, hasMore: false };
  }
}
